/**
 * Flat routes with no `:orgSlug` / `:propertySlug` URL prefix; scope state
 * lives in `ScopeContext` and persists to Capacitor Preferences.
 *
 * Settings split: user-scoped settings live under `/account/*` and are
 * reached from the navbar avatar drawer. Property-scoped settings live
 * under `/property/:propertyId/settings/*` and are reached from the
 * per-property cog in the filter's property facet. Onboarding flows
 * (create / join / new property) live at top-level `/property/*` since
 * there is no property in scope yet.
 */

/**
 * The property settings hubs, each a page of grouped links between the
 * settings root and its pages. A hub's segment must never be a page's.
 */
export const SETTINGS_HUBS = [
  "rooms",
  "stay",
  "messaging",
  "site",
  "compliance",
  "team",
] as const;

export type SettingsHub = (typeof SETTINGS_HUBS)[number];

/**
 * The hub each settings page sits under, by its path segment, so going up
 * from a page lands on that hub. Plain data, because every page header reads
 * it.
 */
export const SETTINGS_HUB_OF_PAGE: ReadonlyMap<string, SettingsHub> = new Map<
  string,
  SettingsHub
>([
  ["activity", "team"],
  ["blocks", "rooms"],
  ["breakfast", "stay"],
  ["cleaning", "team"],
  ["domain", "site"],
  ["emails", "messaging"],
  ["guest-portal", "stay"],
  ["guest-preview", "stay"],
  ["guest-registry", "compliance"],
  ["imports", "rooms"],
  ["integrations", "compliance"],
  ["members", "team"],
  ["parking", "stay"],
  ["payments", "site"],
  ["photos", "site"],
  ["rates", "rooms"],
  ["units", "rooms"],
  ["website", "site"],
]);

export const appRoutes = {
  account: {
    notifications: () => "/account/notifications",
    settings: () => "/account/settings",
  },
  app: {
    calendar: () => "/calendar",
    chats: () => "/chats",
    // Full-screen, outside the tab shell like the task screen: one check-in's
    // record, and a booking's next batch.
    checkIn: (checkInId: string) => `/check-ins/${checkInId}`,
    checkInNew: (reservationId: string) =>
      `/reservations/${reservationId}/check-in`,
    guests: () => "/guests",
    // Top-level (not nested under /calendar): the create flow assumes no
    // starting point.
    newReservation: () => "/reservations/new",
    // Shared create flow: the Tasks tab and the calendar Staff-tab CTA both
    // land here.
    newTask: () => "/tasks/new",
    property: (propertyId: string) => `/property/${propertyId}/settings`,
    propertyActivity: (propertyId: string) =>
      `/property/${propertyId}/settings/activity`,
    propertyBilling: (propertyId: string) =>
      `/property/${propertyId}/settings/billing`,
    propertyBlocks: (propertyId: string) =>
      `/property/${propertyId}/settings/blocks`,
    propertyBreakfast: (propertyId: string) =>
      `/property/${propertyId}/settings/breakfast`,
    propertyCleaning: (propertyId: string) =>
      `/property/${propertyId}/settings/cleaning`,
    propertyCreate: () => "/property/create",
    propertyDomain: (propertyId: string) =>
      `/property/${propertyId}/settings/domain`,
    propertyEmails: (propertyId: string) =>
      `/property/${propertyId}/settings/emails`,
    propertyGuestPortal: (propertyId: string) =>
      `/property/${propertyId}/settings/guest-portal`,
    propertyGuestPreview: (propertyId: string, phase?: string) =>
      `/property/${propertyId}/settings/guest-preview${phase ? `?phase=${phase}` : ""}`,
    propertyGuestRegistry: (propertyId: string) =>
      `/property/${propertyId}/settings/guest-registry`,
    propertyImports: (propertyId: string) =>
      `/property/${propertyId}/settings/imports`,
    propertyInfo: (propertyId: string) =>
      `/property/${propertyId}/settings/info`,
    propertyIntegration: (propertyId: string, system: string) =>
      `/property/${propertyId}/settings/integrations/${system}`,
    propertyJoin: () => "/property/join",
    propertyMemberAvailability: (propertyId: string, staffId: string) =>
      `/property/${propertyId}/settings/members/${staffId}/availability`,
    propertyMembers: (propertyId: string) =>
      `/property/${propertyId}/settings/members`,
    propertyParking: (propertyId: string) =>
      `/property/${propertyId}/settings/parking`,
    propertyParkingEdit: (propertyId: string, parkingId: string) =>
      `/property/${propertyId}/settings/parking/${parkingId}`,
    propertyParkingNew: (propertyId: string) =>
      `/property/${propertyId}/settings/parking/new`,
    propertyPayments: (propertyId: string) =>
      `/property/${propertyId}/settings/payments`,
    propertyPhotos: (propertyId: string) =>
      `/property/${propertyId}/settings/photos`,
    propertyRates: (propertyId: string) =>
      `/property/${propertyId}/settings/rates`,
    propertySettingsHub: (propertyId: string, hub: SettingsHub) =>
      `/property/${propertyId}/settings/${hub}`,
    propertySetup: (propertyId: string) => `/property/${propertyId}/setup`,
    propertyUnitEdit: (propertyId: string, unitCode: string) =>
      `/property/${propertyId}/settings/units/${unitCode}`,
    propertyUnitNew: (propertyId: string) =>
      `/property/${propertyId}/settings/units/new`,
    propertyUnits: (propertyId: string) =>
      `/property/${propertyId}/settings/units`,
    propertyWebsite: (propertyId: string) =>
      `/property/${propertyId}/settings/website`,
    // The Rates view's record of sends, under the Calendar tab so going back
    // lands on the board.
    rateChange: (id: string) =>
      `/calendar/rate-changes/${encodeURIComponent(id)}`,
    task: (taskId: string) => `/tasks/${taskId}`,
    taskLive: (taskId: string) => `/tasks/${taskId}/live`,
    tasks: () => "/tasks",
    // The app's landing screen: the day's arrivals, departures and in-house
    // guests. `/` redirects here (see DefaultAppRedirect).
    today: () => "/today",
  },
  forgotPassword: () => "/forgot-password",
  login: () => "/login",
  resetPassword: () => "/reset-password",
  signup: () => "/signup",
  verifyEmail: () => "/verify-email",
};

export const routeTemplates = {
  account: {
    notifications: "/account/notifications",
    settings: "/account/settings",
  },
  app: {
    activeCall: "/active-call",
    calendar: "/calendar",
    chats: "/chats",
    checkIn: "/check-ins/:id",
    checkInNew: "/reservations/:id/check-in",
    guests: "/guests",
    incomingCall: "/incoming-call",
    newReservation: "/reservations/new",
    newTask: "/tasks/new",
    property: "/property/:propertyId/settings",
    propertyActivity: "/property/:propertyId/settings/activity",
    propertyBilling: "/property/:propertyId/settings/billing",
    propertyBlocks: "/property/:propertyId/settings/blocks",
    propertyBreakfast: "/property/:propertyId/settings/breakfast",
    propertyCleaning: "/property/:propertyId/settings/cleaning",
    propertyCleaningMoved:
      "/property/:propertyId/settings/:page(task-rules|cleaning-checklists)",
    propertyCreate: "/property/create",
    propertyDomain: "/property/:propertyId/settings/domain",
    propertyEmails: "/property/:propertyId/settings/emails",
    propertyGuestPortal: "/property/:propertyId/settings/guest-portal",
    propertyGuestPreview: "/property/:propertyId/settings/guest-preview",
    propertyGuestRegistry: "/property/:propertyId/settings/guest-registry",
    propertyImports: "/property/:propertyId/settings/imports",
    propertyInfo: "/property/:propertyId/settings/info",
    propertyIntegration: "/property/:propertyId/settings/integrations/:system",
    propertyIntegrations: "/property/:propertyId/settings/integrations",
    propertyJoin: "/property/join",
    propertyMemberAvailability:
      "/property/:propertyId/settings/members/:staffId/availability",
    propertyMembers: "/property/:propertyId/settings/members",
    propertyParking: "/property/:propertyId/settings/parking",
    propertyParkingEdit: "/property/:propertyId/settings/parking/:parkingId",
    propertyParkingNew: "/property/:propertyId/settings/parking/new",
    propertyPayments: "/property/:propertyId/settings/payments",
    propertyPhotos: "/property/:propertyId/settings/photos",
    propertyRates: "/property/:propertyId/settings/rates",
    propertyRoot: "/property",
    propertySettingsHub: `/property/:propertyId/settings/:hub(${SETTINGS_HUBS.join("|")})`,
    propertySetup: "/property/:propertyId/setup",
    propertyUnitEdit: "/property/:propertyId/settings/units/:unitCode",
    propertyUnitNew: "/property/:propertyId/settings/units/new",
    propertyUnits: "/property/:propertyId/settings/units",
    propertyWebsite: "/property/:propertyId/settings/website",
    rateChange: "/calendar/rate-changes/:id",
    reservationsRoot: "/reservations",
    task: "/tasks/:taskId",
    taskLive: "/tasks/:id/live",
    tasks: "/tasks",
    today: "/today",
  },
  forgotPassword: "/forgot-password",
  login: "/login",
  resetPassword: "/reset-password",
  signup: "/signup",
  verifyEmail: "/verify-email",
};

/**
 * Hierarchical "up": a settings page goes to the hub that lists it, anything
 * else to its nearest ancestor that is a settings page, else /chats.
 * `…/members/:staffId` and `/property/:id` are path segments, not pages, so
 * up skips them.
 *
 * History-based back would be wrong here: the cross-property pivot pushes
 * URLs, so `goBack()` from Property B would land back on Property A's page
 * instead of going up inside B, and a deep link has no history at all.
 */
const BACK_FALLBACK = "/chats";
const TRAILING_SLASHES = /\/+$/u;
const SETTINGS_PAGE_PREFIX = /^\/property\/:propertyId\/settings/u;
// A param may carry a constraint, as the hubs' `:hub(rooms|stay|…)` does.
const PATH_PARAM = /:\w+(?:\([^)]*\))?/gu;
const SETTINGS_PAGE_OF_PROPERTY =
  /^(?<settingsRoot>\/property\/[^/]+\/settings)\/(?<page>[^/]+)$/u;
const SETTINGS_PAGES = Object.values(routeTemplates.app)
  .filter((template) => SETTINGS_PAGE_PREFIX.test(template))
  .map(
    (template) => new RegExp(`^${template.replace(PATH_PARAM, "[^/]+")}$`, "u")
  );

export function computeParentPath(pathname: string): string {
  const clean = pathname.replace(TRAILING_SLASHES, "");
  const groups = clean.match(SETTINGS_PAGE_OF_PROPERTY)?.groups;
  if (groups) {
    const { settingsRoot, page } = groups;
    const hub = SETTINGS_HUB_OF_PAGE.get(page);
    if (hub) {
      return `${settingsRoot}/${hub}`;
    }
  }
  const segments = clean.split("/");
  const ancestors = segments
    .slice(1, -1)
    .map((_, i) => segments.slice(0, i + 2).join("/"))
    .reverse();
  return (
    ancestors.find((path) =>
      SETTINGS_PAGES.some((template) => template.test(path))
    ) ?? BACK_FALLBACK
  );
}

/** The settings root's entries, by what each one opens. */
export type SettingsEntryKey = "billing" | "general" | SettingsHub;

const lastSegment = (template: string) =>
  template.slice(template.lastIndexOf("/") + 1);
const ENTRY_OF_SEGMENT = new Map<string, SettingsEntryKey>([
  [lastSegment(routeTemplates.app.propertyInfo), "general"],
  [lastSegment(routeTemplates.app.propertyBilling), "billing"],
  ...SETTINGS_HUBS.map((hub): [string, SettingsEntryKey] => [hub, hub]),
]);
const SETTINGS_SEGMENT = /^\/property\/[^/]+\/settings\/(?<segment>[^/]+)/u;

/** The root entry a settings path sits under, or null on the root itself. */
export function settingsEntryOf(pathname: string): SettingsEntryKey | null {
  const groups = pathname.match(SETTINGS_SEGMENT)?.groups;
  if (!groups) {
    return null;
  }
  const { segment } = groups;
  return (
    ENTRY_OF_SEGMENT.get(segment) ?? SETTINGS_HUB_OF_PAGE.get(segment) ?? null
  );
}
